import React, { useMemo } from 'react';
import type {
  ColDef,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  getCurrencyNonSettlementDates,
  saveCurrencyNonSettlementDatesBatch,
} from '../../api/currencyNonSettlementDates';
import { useCrcies } from '../../hooks/useCrcies';
import type { CurrencyNonSettlementDate } from '../../types/CurrencyNonSettlementDate';
import { dateColumnProps } from '../../utils/dateColumn';
import { t } from '../../i18n/t';

const newCurrencyNonSettlementDateDefaults =
  (): Partial<CurrencyNonSettlementDate> => ({
    recId: 0,
    crcyId: 0,
    nonSettlementDate: null as unknown as string,
  });

export function CurrencyNonSettlementDatesPage() {
  // Lazy load: the CRCY lookup list is fetched only when this page mounts.
  const {
    data: crcies,
    loading: crciesLoading,
    error: crciesError,
  } = useCrcies();

  const columnDefs = useMemo<ColDef<CurrencyNonSettlementDate>[]>(() => {
    if (!crcies) return [];

    // Currency names: REQUIRED FK — no none option. Keyed by a unique
    // "<id> - <name>" label (mirrors AssetsPage crcyId, but required).
    const crcyValues = crcies.map((c) => `${c.crcyId} - ${c.crcyName}`);
    const crcyLabelById = new Map<number, string>(
      crcies.map((c) => [c.crcyId, `${c.crcyId} - ${c.crcyName}`]),
    );
    const crcyIdByLabel = new Map<string, number>(
      crcies.map((c) => [`${c.crcyId} - ${c.crcyName}`, c.crcyId]),
    );

    return [
      {
        field: 'recId',
        headerName: t('currencyNonSettlementDates.columns.recId'),
        width: 110,
        // IDENTITY PK assigned by the server — never editable.
        editable: false,
        cellDataType: 'number',
        sort: 'asc',
      },
      {
        field: 'crcyId',
        headerName: t('currencyNonSettlementDates.columns.crcyId'),
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: crcyValues },
        valueGetter: (p: ValueGetterParams<CurrencyNonSettlementDate>) => {
          if (!p.data) return '';
          // crcyId is REQUIRED (NOT NULL) — no none option. Fall back to the
          // raw id as a string for dirty data not present in the lookup.
          return crcyLabelById.get(p.data.crcyId) ?? String(p.data.crcyId);
        },
        valueSetter: (p: ValueSetterParams<CurrencyNonSettlementDate>) => {
          if (!p.data) return false;
          const newId = crcyIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.crcyId = newId;
          return true;
        },
      },
      {
        field: 'nonSettlementDate',
        headerName: t('currencyNonSettlementDates.columns.nonSettlementDate'),
        width: 160,
        editable: true,
        ...dateColumnProps<CurrencyNonSettlementDate>('nonSettlementDate'),
      },
    ];
  }, [crcies]);

  if (crciesError) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('currencyNonSettlementDates.errorPrefix', {
            message: crciesError,
          })}
        </div>
      </div>
    );
  }

  // Render the grid once the CRCY lookup is loaded.
  if (crciesLoading || !crcies) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <LopCrudPage<CurrencyNonSettlementDate>
      resourceCode="currencyNonSettlementDates"
      titleKey="currencyNonSettlementDates.pageTitle"
      idField="recId"
      getRows={getCurrencyNonSettlementDates}
      saveBatch={(added, modified, deletedIds) =>
        saveCurrencyNonSettlementDatesBatch(
          added,
          modified,
          deletedIds as number[],
        )
      }
      columnDefs={columnDefs}
      newRowDefaults={newCurrencyNonSettlementDateDefaults}
    />
  );
}
